/* Masidas theme — interactions. No dependencies. */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {}, cartType: 'drawer', delivery: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Runs fn on the next frame, or after 120ms if the browser is not drawing frames (background tabs)
  const nextFrame = (fn) => { let done = false; const run = () => { if (!done) { done = true; fn(); } }; requestAnimationFrame(run); setTimeout(run, 120); };

  /* ---------- Money: same output as Liquid's money_without_trailing_zeros ---------- */
  function formatMoney(cents) {
    const fmt = theme.moneyFormat || '${{amount}}';
    const n = Number(cents) / 100;
    const group = (num, decimals, thousands, separator) => {
      const [i, d] = num.toFixed(decimals).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (d ? separator + d : '');
    };
    const out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(n, 0, ',', '.');
        case 'amount_with_comma_separator': return group(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(n, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(n, 2, "'", '.');
        default: return group(n, 2, ',', '.');
      }
    });
    return out.replace(/([.,])00(?!\d)/, '');
  }
  const addLabel = (cents) => (theme.strings.addToCartPrice ? theme.strings.addToCartPrice.replace('[price]', formatMoney(cents)) : theme.strings.addToCart);

  /* ---------- Toast ---------- */
  const toastEl = () => $('[data-toast]');
  function toast(message) {
    const el = toastEl();
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => el.classList.remove('is-visible'), 2400);
  }

  /* ---------- Panels: drawers, search, size guide ---------- */
  const panels = {
    active: null,
    lastFocus: null,
    overlay: () => $('[data-overlay]'),
    open(id, trigger) {
      const panel = document.getElementById(id);
      if (!panel) return false;
      if (this.active && this.active !== panel) this.close(true);
      this.lastFocus = trigger || document.activeElement;
      panel.hidden = false;
      // allow the browser to paint the hidden state before animating in
      nextFrame(() => nextFrame(() => panel.classList.add('is-open')));
      panel.setAttribute('aria-hidden', 'false');
      if (!panel.matches('.search-panel')) {
        const ov = this.overlay();
        if (ov) { ov.hidden = false; nextFrame(() => ov.classList.add('is-visible')); }
      }
      document.body.classList.add('scroll-lock');
      $$(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      this.active = panel;
      document.dispatchEvent(new CustomEvent('panel:open', { detail: { id } }));
      setTimeout(() => {
        const target = panel.querySelector('[data-autofocus]') || panel.querySelector('input:not([type=hidden]), [tabindex="-1"], button, a');
        if (target) target.focus({ preventScroll: true });
      }, 80);
      return true;
    },
    close(silent) {
      const panel = this.active;
      if (!panel) return;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      $$(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      const ov = this.overlay();
      if (ov) { ov.classList.remove('is-visible'); setTimeout(() => { if (!this.active) ov.hidden = true; }, 320); }
      document.body.classList.remove('scroll-lock');
      setTimeout(() => { if (this.active !== panel) panel.hidden = true; }, 460);
      this.active = null;
      document.dispatchEvent(new CustomEvent('panel:close', { detail: { id: panel.id } }));
      if (!silent && this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const id = opener.getAttribute('data-open');
      if (id === 'CartDrawer' && theme.cartType !== 'drawer') return; // fall back to the cart page link
      if (document.getElementById(id)) { e.preventDefault(); panels.open(id, opener); }
      return;
    }
    if (e.target.closest('[data-close]') || e.target.matches('[data-overlay]')) { e.preventDefault(); panels.close(); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (panels.active) panels.close();
      $$('details.dropdown[open]').forEach((d) => d.removeAttribute('open'));
    }
    if (e.key === 'Tab' && panels.active) {
      const focusables = $$('a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select, textarea, [tabindex="0"]', panels.active).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  // Close desktop dropdowns when clicking elsewhere
  document.addEventListener('click', (e) => {
    $$('details.dropdown[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });

  /* ---------- Header: hide on scroll, overlay becomes solid ---------- */
  function initHeader() {
    const wrap = $('.section-header');
    if (!wrap || wrap.dataset.ready) return;
    wrap.dataset.ready = 'true';
    const hide = !!wrap.querySelector('[data-hide-on-scroll]');
    const overlay = !!wrap.querySelector('.header--overlay');
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      const goingDown = y > lastY;
      if (hide) {
        const shouldHide = goingDown && y > 240 && !panels.active;
        wrap.classList.toggle('is-hidden', shouldHide);
        document.body.classList.toggle('header-hidden', shouldHide);
      }
      if (overlay) wrap.classList.toggle('is-solid', y > 40 || !!panels.active || wrap.matches(':hover') || wrap.matches(':focus-within'));
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; nextFrame(update); } }, { passive: true });
    if (overlay) {
      ['mouseenter', 'mouseleave', 'focusin', 'focusout'].forEach((ev) => wrap.addEventListener(ev, () => nextFrame(update)));
      document.addEventListener('panel:open', update);
      document.addEventListener('panel:close', () => setTimeout(update, 50));
    }
    update();
  }

  /* ---------- Cart ---------- */
  const cart = {
    sectionIds() {
      const ids = ['cart-count'];
      if (document.getElementById('shopify-section-cart-drawer')) ids.push('cart-drawer');
      const main = $('[data-main-cart]');
      if (main) ids.push(main.dataset.sectionId);
      return ids;
    },
    async add(body) {
      body.append('sections', this.sectionIds().join(','));
      body.append('sections_url', window.location.pathname);
      const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.render(data.sections);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.render(data.sections);
      return data;
    },
    render(sections) {
      if (!sections) return;
      if (sections['cart-count'] !== undefined) {
        const count = parse(sections['cart-count']).body.textContent.trim();
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = count;
          el.classList.remove('bump');
          void el.offsetWidth;
          el.classList.add('bump');
        });
      }
      if (sections['cart-drawer']) {
        const fresh = parse(sections['cart-drawer']).getElementById('CartDrawer');
        const drawer = document.getElementById('CartDrawer');
        if (fresh && drawer) {
          const hadFocus = drawer.contains(document.activeElement);
          drawer.innerHTML = fresh.innerHTML;
          if (hadFocus) { const h = drawer.querySelector('[data-autofocus]'); if (h) h.focus({ preventScroll: true }); }
        }
      }
      const main = $('[data-main-cart]');
      if (main && sections[main.dataset.sectionId]) {
        const fresh = parse(sections[main.dataset.sectionId]).querySelector('[data-main-cart]');
        if (fresh) main.innerHTML = fresh.innerHTML;
      }
    },
    afterAdd() {
      if (theme.cartType === 'drawer' && document.getElementById('CartDrawer')) panels.open('CartDrawer');
      else toast(theme.strings.addedToast);
    }
  };

  function setBusy(button, busy) {
    if (!button) return;
    if (busy) button.setAttribute('aria-busy', 'true'); else button.removeAttribute('aria-busy');
  }

  /* Quick add on product cards */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    const body = new FormData();
    body.append('id', btn.getAttribute('data-quick-add'));
    body.append('quantity', '1');
    btn.disabled = true;
    try {
      await cart.add(body);
      const card = btn.closest('.card');
      cart.afterAdd(card ? (card.querySelector('.card__title') || {}).textContent : '');
    } catch (err) {
      toast(err.message);
    } finally {
      btn.disabled = false;
    }
  });

  /* Quantity and remove inside the drawer or cart page */
  const changeLine = async (control, quantity) => {
    const line = parseInt(control.getAttribute('data-line'), 10);
    const row = control.closest('.cart-line');
    if (row) row.classList.add('is-loading');
    try { await cart.change(line, quantity); }
    catch (err) { toast(err.message); if (row) row.classList.remove('is-loading'); }
  };
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (step) {
      e.preventDefault();
      const input = step.parentElement.querySelector('input');
      const next = Math.max(0, parseInt(input.value || '0', 10) + parseInt(step.getAttribute('data-qty-step'), 10));
      input.value = next;
      changeLine(step, next);
      return;
    }
    const remove = e.target.closest('[data-remove-line]');
    if (remove) { e.preventDefault(); changeLine(remove, 0); }
  });
  document.addEventListener('change', debounce((e) => {
    const input = e.target.closest('.cart-line .qty input');
    if (input) changeLine(input, Math.max(0, parseInt(input.value || '0', 10)));
  }, 350));

  /* ---------- Product form ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.querySelector('[data-atc]');
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const idInput = this.form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) {
        const picker = document.querySelector(`variant-picker[data-section="${this.dataset.section}"]`);
        if (picker) picker.flagMissing();
        return;
      }
      if (this.button.getAttribute('aria-busy') === 'true') return;
      setBusy(this.button, true);
      if (this.error) this.error.textContent = '';
      try {
        await cart.add(new FormData(this.form));
        this.button.dataset.state = 'added';
        const label = this.button.querySelector('.button__label');
        const previous = label ? label.textContent : '';
        if (label) label.textContent = theme.strings.added;
        setTimeout(() => { delete this.button.dataset.state; if (label) label.textContent = previous; }, 1600);
        cart.afterAdd(this.dataset.productTitle);
      } catch (err) {
        if (this.error) this.error.textContent = err.message;
      } finally {
        setBusy(this.button, false);
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.section = this.dataset.section;
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    selected() {
      return $$('fieldset', this).map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    flagMissing() {
      const missing = $$('fieldset', this).find((fs) => !fs.querySelector('input:checked'));
      const hint = document.getElementById(`BuyHint-${this.section}`);
      if (hint) hint.textContent = theme.strings.chooseSize;
      if (missing) { missing.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' }); const first = missing.querySelector('input'); if (first) first.focus({ preventScroll: true }); }
    }
    onChange() {
      const values = this.selected();
      $$('fieldset', this).forEach((fs, i) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = values[i] || '';
      });
      this.updateAvailability();
      const hint = document.getElementById(`BuyHint-${this.section}`);
      if (hint) hint.textContent = '';
      if (values.includes(null)) return;
      const variant = this.variants.find((v) => v.options.every((o, i) => o === values[i]));
      const idInput = document.querySelector(`#ProductForm-${this.section} [name="id"]`);
      const button = document.querySelector(`#ProductForm-${this.section} [data-atc]`);
      const stickyBtn = document.querySelector(`[data-sticky-atc="${this.section}"] .button`);
      if (!variant) {
        if (idInput) idInput.value = '';
        this.setButton(button, false, theme.strings.unavailable);
        this.setButton(stickyBtn, false, theme.strings.unavailable);
        return;
      }
      if (idInput) idInput.value = variant.id;
      this.setButton(button, variant.available, variant.available ? addLabel(variant.price) : theme.strings.soldOut);
      this.setButton(stickyBtn, variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      this.showMedia(variant);
      this.refreshPrice(variant.id);
    }
    setButton(button, enabled, text) {
      if (!button) return;
      button.disabled = !enabled;
      const label = button.querySelector('.button__label') || button;
      label.textContent = text;
    }
    updateAvailability() {
      const values = this.selected();
      $$('fieldset', this).forEach((fs, index) => {
        $$('input', fs).forEach((input) => {
          // A value is available if some in-stock variant has it, combined with the other options already chosen
          const available = this.variants.some((v) => v.available && v.options.every((o, i) => {
            if (i === index) return o === input.value;
            return values[i] === null || o === values[i];
          }));
          input.closest('.option__value').classList.toggle('option__value--unavailable', !available);
        });
      });
    }
    showMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.getElementById(`Gallery-${this.section}`);
      if (!gallery) return;
      const item = gallery.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (!item) return;
      if (window.matchMedia('(min-width: 761px)').matches) {
        if (gallery.firstElementChild !== item) gallery.prepend(item);
      } else {
        gallery.scrollTo({ left: item.offsetLeft - gallery.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }
    async refreshPrice(variantId) {
      try {
        const res = await fetch(`${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.sectionId}`);
        const doc = parse(await res.text());
        ['Price', 'Inventory'].forEach((key) => {
          const fresh = doc.getElementById(`${key}-${this.section}`);
          const current = document.getElementById(`${key}-${this.section}`);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
      } catch (err) { /* price stays as is */ }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Gallery: zoom on desktop, counter on mobile ---------- */
  document.addEventListener('click', (e) => {
    const item = e.target.closest('.gallery__item[data-zoomable]');
    if (!item || !window.matchMedia('(hover: hover)').matches) return;
    item.classList.toggle('is-zoomed');
    if (item.classList.contains('is-zoomed')) {
      const img = item.querySelector('img');
      const rect = item.getBoundingClientRect();
      if (img) img.style.transformOrigin = `${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`;
    }
  });
  document.addEventListener('mousemove', (e) => {
    const item = e.target.closest('.gallery__item.is-zoomed');
    if (!item) return;
    const img = item.querySelector('img');
    const rect = item.getBoundingClientRect();
    if (img) img.style.transformOrigin = `${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`;
  });
  function initGalleryCounters() {
    $$('.gallery').forEach((gallery) => {
      const counter = gallery.parentElement.querySelector('.gallery__count');
      if (!counter) return;
      const items = $$('.gallery__item', gallery);
      gallery.addEventListener('scroll', debounce(() => {
        const index = Math.round(gallery.scrollLeft / (items[0] ? items[0].offsetWidth : 1));
        counter.textContent = `${Math.min(index + 1, items.length)} / ${items.length}`;
      }, 60), { passive: true });
    });
  }

  /* ---------- Sticky add to bag ---------- */
  function initStickyAtc() {
    $$('[data-sticky-atc]').forEach((bar) => {
      const section = bar.getAttribute('data-sticky-atc');
      const main = document.querySelector(`#ProductForm-${section} [data-atc]`);
      if (!main || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        $$('button', bar).forEach((b) => { b.tabIndex = show ? 0 : -1; });
      });
      io.observe(main);
      bar.querySelector('.button').addEventListener('click', () => {
        const form = document.getElementById(`ProductForm-${section}`);
        if (form) form.requestSubmit();
      });
    });
  }

  /* ---------- Delivery estimate ---------- */
  function addBusinessDays(date, days) {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d;
  }
  function initDelivery() {
    const cfg = theme.delivery || {};
    $$('[data-delivery-estimate]').forEach((el) => {
      const now = new Date();
      const start = addBusinessDays(now, (cfg.processing || 0) + (cfg.min || 0));
      const end = addBusinessDays(now, (cfg.processing || 0) + (cfg.max || 0));
      let fmt;
      try { fmt = new Intl.DateTimeFormat(cfg.locale || undefined, { month: 'short', day: 'numeric' }); } catch (err) { fmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }); }
      let endText = fmt.format(end);
      if (start.getMonth() === end.getMonth()) { try { endText = new Intl.DateTimeFormat(cfg.locale || undefined, { day: 'numeric' }).format(end); } catch (err) { /* keep full date */ } }
      el.textContent = el.getAttribute('data-template').replace('[start]', fmt.format(start)).replace('[end]', endText);
    });
  }

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('#PredictiveResults');
      if (!this.input) return;
      this.input.addEventListener('input', debounce(() => this.search(this.input.value.trim()), 220));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.addEventListener('click', (e) => {
        const s = e.target.closest('[data-suggest]');
        if (s) { this.input.value = s.getAttribute('data-suggest'); this.search(this.input.value); this.input.focus(); }
      });
    }
    async search(q) {
      if (this.controller) this.controller.abort();
      if (!q) { this.results.innerHTML = ''; this.input.setAttribute('aria-expanded', 'false'); return; }
      this.controller = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) return;
        const doc = parse(await res.text());
        const fresh = doc.querySelector('[data-predictive]');
        this.results.innerHTML = fresh ? fresh.outerHTML : '';
        this.input.setAttribute('aria-expanded', 'true');
        this.index = -1;
      } catch (err) { /* aborted or offline */ }
    }
    onKey(e) {
      const options = $$('[role="option"]', this.results);
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      if (e.key === 'Enter') {
        if (this.index >= 0 && options[this.index]) { e.preventDefault(); options[this.index].querySelector('a').click(); }
        return;
      }
      e.preventDefault();
      this.index = typeof this.index === 'number' ? this.index : -1;
      this.index = (this.index + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.index)));
      this.input.setAttribute('aria-activedescendant', options[this.index].id);
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Collection filters, sort and load more ---------- */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.sectionId = this.dataset.sectionId;
      if (!this.form) return;
      const run = debounce(() => this.apply(this.queryFromForm()), 380);
      this.form.addEventListener('input', run);
      document.addEventListener('change', (e) => { if (e.target.matches('[data-sort]')) this.apply(this.queryFromForm()); });
      document.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-facet-link]');
        if (!remove) return;
        e.preventDefault();
        this.apply(new URL(remove.href, window.location.origin).searchParams.toString());
      });
    }
    queryFromForm() {
      const data = new FormData(this.form);
      const sort = document.querySelector('[data-sort]');
      if (sort && sort.value) data.set('sort_by', sort.value);
      const params = new URLSearchParams();
      for (const [k, v] of data.entries()) if (v !== '') params.append(k, v);
      return params.toString();
    }
    async apply(query) {
      const results = document.getElementById(`CollectionResults-${this.sectionId}`);
      if (results) results.classList.add('is-loading');
      try {
        const res = await fetch(`${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.sectionId}`);
        const doc = parse(await res.text());
        const openIds = $$('details[open]', this.form).map((d) => d.id);
        ['CollectionResults', 'FacetsBody', 'ActiveFilters', 'ProductCount'].forEach((key) => {
          const fresh = doc.getElementById(`${key}-${this.sectionId}`);
          const current = document.getElementById(`${key}-${this.sectionId}`);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        openIds.forEach((id) => { const d = document.getElementById(id); if (d) d.open = true; });
        window.history.replaceState({}, '', `${window.location.pathname}${query ? '?' + query : ''}`);
      } catch (err) {
        toast(theme.strings.cartError);
      } finally {
        if (results) results.classList.remove('is-loading');
      }
    }
  }
  if (!customElements.get('facet-form')) customElements.define('facet-form', FacetForm);

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-load-more]');
    if (!btn) return;
    e.preventDefault();
    const grid = document.getElementById(btn.getAttribute('data-grid'));
    const url = btn.getAttribute('data-next-url');
    if (!grid || !url) return;
    setBusy(btn, true);
    try {
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${btn.getAttribute('data-section-id')}`);
      const doc = parse(await res.text());
      const freshGrid = doc.getElementById(grid.id);
      if (freshGrid) $$(':scope > *', freshGrid).forEach((item) => grid.appendChild(item));
      const freshBtn = doc.querySelector('[data-load-more]');
      if (freshBtn) btn.setAttribute('data-next-url', freshBtn.getAttribute('data-next-url'));
      else btn.closest('.load-more').remove();
      window.history.replaceState({}, '', url);
    } catch (err) {
      window.location.href = url;
    } finally {
      setBusy(btn, false);
    }
  });

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) { this.load(); return; }
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) { io.disconnect(); this.load(); }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
    async load() {
      try {
        const res = await fetch(this.dataset.url);
        const doc = parse(await res.text());
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.querySelector('.card')) this.innerHTML = fresh.innerHTML;
        else this.closest('.shopify-section').hidden = true;
      } catch (err) { /* leave section empty */ }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Localization selects submit on change ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-localization-select]')) e.target.form.submit();
  });

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', () => { initHeader(); initStickyAtc(); initDelivery(); initGalleryCounters(); });
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });

  /* ---------- Boot ---------- */
  const boot = () => { initHeader(); initStickyAtc(); initDelivery(); initGalleryCounters(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
